import { z } from 'zod';
import { pageQuerySchema } from './marketplace.js';
/**
 * In-app notifications.
 *
 * Until now the only thing that reached a user between sessions was
 * transactional email — OTP, guardian invite, consent. That leaves a teen
 * refreshing the page to find out whether they got the job. These contracts
 * back a real inbox, fed by the domain events the outbox already records.
 * [Teen step 19, Parent step 19]
 */
/* -------------------------------------------------------------------------- */
/* Types and categories                                                       */
/* -------------------------------------------------------------------------- */
/**
 * What happened. Stored as a string in the database, so this list can grow
 * without a migration — but an unknown value must never crash a client, which
 * is why `notificationSchema` falls back rather than rejecting.
 */
export const notificationTypeSchema = z.enum([
    'CONSENT_REQUESTED',
    'CONSENT_APPROVED',
    'CONSENT_DECLINED',
    'JOB_SHARED',
    'APPLICATION_SUBMITTED',
    'APPLICATION_SHORTLISTED',
    'APPLICATION_DECLINED',
    'ASSIGNMENT_OFFERED',
    'ASSIGNMENT_ACCEPTED',
    'ASSIGNMENT_DECLINED',
    'ASSIGNMENT_COMPLETED',
    'ASSIGNMENT_APPROVED',
    'PAYMENT_RELEASED',
    'BID_SUBMITTED',
    'BID_ACCEPTED',
    'BID_REJECTED',
    'MESSAGE_RECEIVED',
    'REVIEW_RECEIVED',
    'SYSTEM',
]);
/**
 * The grouping a person actually recognises. Preferences are expressed per
 * category, not per type — nobody wants nineteen toggles.
 */
export const notificationCategorySchema = z.enum([
    'CONSENT',
    'APPLICATIONS',
    'ASSIGNMENTS',
    'MESSAGES',
    'PAYMENTS',
    'REVIEWS',
    'SYSTEM',
]);
/** Which toggle silences which event. */
export const NOTIFICATION_CATEGORY = {
    CONSENT_REQUESTED: 'CONSENT',
    CONSENT_APPROVED: 'CONSENT',
    CONSENT_DECLINED: 'CONSENT',
    JOB_SHARED: 'APPLICATIONS',
    APPLICATION_SUBMITTED: 'APPLICATIONS',
    APPLICATION_SHORTLISTED: 'APPLICATIONS',
    APPLICATION_DECLINED: 'APPLICATIONS',
    ASSIGNMENT_OFFERED: 'ASSIGNMENTS',
    ASSIGNMENT_ACCEPTED: 'ASSIGNMENTS',
    ASSIGNMENT_DECLINED: 'ASSIGNMENTS',
    ASSIGNMENT_COMPLETED: 'ASSIGNMENTS',
    ASSIGNMENT_APPROVED: 'ASSIGNMENTS',
    PAYMENT_RELEASED: 'PAYMENTS',
    BID_SUBMITTED: 'APPLICATIONS',
    BID_ACCEPTED: 'APPLICATIONS',
    BID_REJECTED: 'APPLICATIONS',
    MESSAGE_RECEIVED: 'MESSAGES',
    REVIEW_RECEIVED: 'REVIEWS',
    SYSTEM: 'SYSTEM',
};
export const NOTIFICATION_CATEGORY_LABEL = {
    CONSENT: 'Guardian approvals',
    APPLICATIONS: 'Applications and bids',
    ASSIGNMENTS: 'Jobs and assignments',
    MESSAGES: 'Messages',
    PAYMENTS: 'Payments',
    REVIEWS: 'Reviews',
    SYSTEM: 'Account and security',
};
/* -------------------------------------------------------------------------- */
/* Reading                                                                    */
/* -------------------------------------------------------------------------- */
export const notificationSchema = z.object({
    id: z.string(),
    /**
     * Unknown values degrade to SYSTEM rather than failing the whole page — a
     * server that learns a new type must not break an older client.
     */
    type: z.string().transform((value) => {
        const parsed = notificationTypeSchema.safeParse(value);
        return parsed.success ? parsed.data : 'SYSTEM';
    }),
    title: z.string(),
    message: z.string(),
    /** Where clicking it goes, as an app-relative path. Null when nowhere. */
    link: z.string().nullable(),
    isRead: z.boolean(),
    createdAt: z.string(),
});
export const notificationListQuerySchema = pageQuerySchema.extend({
    /** `true` narrows to the unread ones — what the bell opens to. */
    unreadOnly: z.coerce.boolean().optional(),
    category: notificationCategorySchema.optional(),
});
export const unreadCountSchema = z.object({
    unread: z.number().int().min(0),
});
/* -------------------------------------------------------------------------- */
/* Preferences                                                                */
/* -------------------------------------------------------------------------- */
/**
 * Stored as JSON on `notification_preferences.prefs`, so an absent row means
 * "everything on" rather than "nothing configured". [Parent step 3]
 */
export const notificationPreferencesSchema = z.object({
    emailEnabled: z.boolean(),
    pushEnabled: z.boolean(),
    inAppEnabled: z.boolean(),
    /** Categories the person has silenced. In-app delivery is never silenced. */
    mutedCategories: z.array(notificationCategorySchema),
});
export const DEFAULT_NOTIFICATION_PREFERENCES = {
    emailEnabled: true,
    pushEnabled: false,
    inAppEnabled: true,
    mutedCategories: [],
};
export const updateNotificationPreferencesInputSchema = notificationPreferencesSchema.partial();
export const markReadInputSchema = z.object({
    /** Omit to mark the whole inbox read. */
    notificationIds: z.array(z.string()).min(1).max(200).optional(),
});
//# sourceMappingURL=notifications.js.map